import { CommanUsesService } from './../../services/comman-uses.service';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

import { CalendarOptions } from '@fullcalendar/angular';
import { AvailabilityOwnerType, SaveAvailabilityPayload } from 'src/app/constants/availability.types';
import { AvailabilityService } from '../../services/availability.service';

@Component({
  selector: 'app-availability-editor',
  templateUrl: './availability-editor.component.html',
  styleUrls: ['./availability-editor.component.scss'],
})
export class AvailabilityEditorComponent implements OnChanges {
  @Input() ownerId!: string | number;
  @Input() ownerType!: AvailabilityOwnerType;

  activeTab: 'weekly' | 'special' | 'calendar' = 'weekly';

  selectedDay = 1;
  selectedOverrideIndex = -1;
  newOverrideDate = '';
  copyFromDayOfWeek: number | null = null;

  loading = false;
  saving = false;

  currentCalendarStart!: string;
  currentCalendarEnd!: string;

  days = [
    { label: 'Mon', name: 'Monday', dayOfWeek: 1 },
    { label: 'Tue', name: 'Tuesday', dayOfWeek: 2 },
    { label: 'Wed', name: 'Wednesday', dayOfWeek: 3 },
    { label: 'Thu', name: 'Thursday', dayOfWeek: 4 },
    { label: 'Fri', name: 'Friday', dayOfWeek: 5 },
    { label: 'Sat', name: 'Saturday', dayOfWeek: 6 },
    { label: 'Sun', name: 'Sunday', dayOfWeek: 7 },
  ];

  availabilityForm!: FormGroup;

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    height: 'auto',
    dateClick: this.handleCalendarDateClick.bind(this),
    eventClick: this.handleCalendarEventClick.bind(this),
    datesSet: this.handleCalendarDatesSet.bind(this),
    events: [],
    eventDisplay: 'block',
    displayEventTime: false,
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly availabilityService: AvailabilityService,
    private readonly commanUsesService: CommanUsesService,
  ) {
    this.buildBlankForm();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (
      (changes.ownerId || changes.ownerType) &&
      this.ownerId &&
      this.ownerType
    ) {
      this.buildBlankForm();
      await this.loadAvailability();
    }
  }

  buildBlankForm(): void {
    this.availabilityForm = this.fb.group({
      weekly: this.fb.array(
        this.days.map(day => this.createWeeklyDayGroup(day.dayOfWeek)),
      ),
      overrides: this.fb.array([]),
    });

    this.selectedDay = 1;
    this.selectedOverrideIndex = -1;
    this.refreshCalendarEvents();
  }

  createWeeklyDayGroup(dayOfWeek: number): FormGroup {
    return this.fb.group({
      id: [null],
      dayOfWeek: [dayOfWeek, Validators.required],
      isClosed: [false],
      slots: this.fb.array([]),
    });
  }

  createOverrideGroup(data?: any): FormGroup {
    const group = this.fb.group({
      id: [data?.id || null],
      date: [data?.date || '', Validators.required],
      isClosed: [data?.isClosed || false],
      note: [data?.note || ''],
      slots: this.fb.array([]),
    });

    const slotsArray = group.get('slots') as FormArray;

    if (data?.slots?.length) {
      data.slots.forEach(slot => {
        slotsArray.push(this.createSlotGroup(slot));
      });
    }

    return group;
  }

  createSlotGroup(slot?: any): FormGroup {
    return this.fb.group({
      start: [slot?.start || '', Validators.required],
      end: [slot?.end || '', Validators.required],
    });
  }

  get weeklyArray(): FormArray {
    return this.availabilityForm.get('weekly') as FormArray;
  }

  get overridesArray(): FormArray {
    return this.availabilityForm.get('overrides') as FormArray;
  }

  get selectedWeeklyIndex(): number {
    return this.selectedDay - 1;
  }

  get selectedWeeklyGroup(): FormGroup {
    return this.weeklyArray.at(this.selectedDay - 1) as FormGroup;
  }

  get selectedWeeklySlots(): FormArray {
    return this.selectedWeeklyGroup.get('slots') as FormArray;
  }

  get selectedOverrideGroup(): FormGroup | null {
    if (this.selectedOverrideIndex < 0) return null;
    return this.overridesArray.at(this.selectedOverrideIndex) as FormGroup;
  }

  get selectedOverrideSlots(): FormArray {
    return this.selectedOverrideGroup?.get('slots') as FormArray;
  }

  get selectedDayName(): string {
    return this.days.find(day => day.dayOfWeek === this.selectedDay)?.name || '';
  }

  selectWeeklyDay(dayOfWeek: number): void {
    this.selectedDay = dayOfWeek;
  }

  selectOverride(index: number): void {
    this.selectedOverrideIndex = index;
  }

  async loadAvailability(): Promise<void> {
    try {
      this.loading = true;

      const res = await this.availabilityService.getAvailability(
        this.ownerType,
        this.ownerId,
      );

      this.patchAvailability(res);
    } catch (error) {
      console.error(error);
      this.buildBlankForm();
    } finally {
      this.loading = false;
    }
  }

  patchAvailability(data: any): void {
    this.weeklyArray.clear();
    this.overridesArray.clear();

    const savedWeekly = data?.weekly || [];
    const savedOverrides = data?.overrides || [];

    this.days.forEach(day => {
      const saved = savedWeekly.find(item => item.dayOfWeek === day.dayOfWeek);

      const dayGroup = this.fb.group({
        id: [saved?.id || null],
        dayOfWeek: [day.dayOfWeek, Validators.required],
        isClosed: [saved?.isClosed || false],
        slots: this.fb.array([]),
      });

      const slotsArray = dayGroup.get('slots') as FormArray;

      if (saved?.slots?.length) {
        saved.slots.forEach(slot => {
          slotsArray.push(this.createSlotGroup(slot));
        });
      }

      this.weeklyArray.push(dayGroup);
    });

    savedOverrides.forEach(item => {
      this.overridesArray.push(this.createOverrideGroup(item));
    });

    this.selectedOverrideIndex = this.overridesArray.length ? 0 : -1;
    this.availabilityForm.markAsPristine();
    this.refreshCalendarEvents();
  }

  showTab(tab: 'weekly' | 'special' | 'calendar'): void {
    this.activeTab = tab;

    if (tab === 'calendar') {
      this.refreshCalendarEvents();
    }
  }

  setWeeklyClosed(value: boolean): void {
    const group = this.selectedWeeklyGroup;
    const slots = group.get('slots') as FormArray;

    group.patchValue(
      {
        isClosed: value,
      },
      { emitEvent: false },
    );

    if (value) {
      slots.clear();
    }

    this.availabilityForm.markAsDirty();
    this.refreshCalendarEvents();
  }

  addWeeklySlot(): void {
    this.selectedWeeklyGroup.patchValue({ isClosed: false });
    this.selectedWeeklySlots.push(this.createSlotGroup());

    this.availabilityForm.markAsDirty();
    this.refreshCalendarEvents();
  }

  removeWeeklySlot(index: number): void {
    this.selectedWeeklySlots.removeAt(index);

    this.availabilityForm.markAsDirty();
    this.refreshCalendarEvents();
  }

  addOverride(): void {
    if (!this.newOverrideDate) {
      this.commanUsesService.showSnackBar('Please select a date.');
      return;
    }

    const exists = this.overridesArray.controls.some(control => {
      return control.get('date')?.value === this.newOverrideDate;
    });

    if (exists) {
      this.commanUsesService.showSnackBar('Special date already exists.');
      return;
    }

    this.overridesArray.push(
      this.createOverrideGroup({
        date: this.newOverrideDate,
        isClosed: false,
        note: '',
        slots: [],
      }),
    );

    this.sortOverrides();

    this.selectedOverrideIndex = this.overridesArray.controls.findIndex(
      control => control.get('date')?.value === this.newOverrideDate,
    );

    this.newOverrideDate = '';
    this.availabilityForm.markAsDirty();
    this.refreshCalendarEvents();
  }

  addOverrideFromCalendar(date: string): void {
    const existingIndex = this.overridesArray.controls.findIndex(control => {
      return control.get('date')?.value === date;
    });

    this.activeTab = 'special';

    // If this date already has a special override, open it.
    if (existingIndex >= 0) {
      this.selectedOverrideIndex = existingIndex;
      this.newOverrideDate = '';
      return;
    }

    // Do NOT create an empty override just by clicking the calendar.
    // Only prefill the date. User must click "+ Aggiungi Data".
    this.selectedOverrideIndex = -1;
    this.newOverrideDate = date;
  }

  setOverrideClosed(value: boolean): void {
    if (!this.selectedOverrideGroup) return;

    const slots = this.selectedOverrideGroup.get('slots') as FormArray;

    this.selectedOverrideGroup.patchValue(
      {
        isClosed: value,
      },
      { emitEvent: false },
    );

    if (value) {
      slots.clear();
    }

    this.availabilityForm.markAsDirty();
    this.refreshCalendarEvents();
  }

  addOverrideSlot(): void {
    if (!this.selectedOverrideGroup) return;

    this.selectedOverrideGroup.patchValue({ isClosed: false });
    this.selectedOverrideSlots.push(this.createSlotGroup());

    this.availabilityForm.markAsDirty();
    this.refreshCalendarEvents();
  }

  removeOverrideSlot(index: number): void {
    if (!this.selectedOverrideGroup) return;

    this.selectedOverrideSlots.removeAt(index);

    this.availabilityForm.markAsDirty();
    this.refreshCalendarEvents();
  }

  deleteOverride(): void {
    if (this.selectedOverrideIndex < 0) return;

    this.overridesArray.removeAt(this.selectedOverrideIndex);
    this.selectedOverrideIndex = this.overridesArray.length ? 0 : -1;

    this.availabilityForm.markAsDirty();
    this.refreshCalendarEvents();
  }

  updateOverrideDate(value: string): void {
    if (!this.selectedOverrideGroup) return;

    const duplicate = this.overridesArray.controls.some((control, index) => {
      return (
        control.get('date')?.value === value &&
        index !== this.selectedOverrideIndex
      );
    });

    if (duplicate) {
      this.commanUsesService.showSnackBar('Special date already exists.');
      return;
    }

    this.selectedOverrideGroup.patchValue({ date: value });

    this.sortOverrides();

    this.selectedOverrideIndex = this.overridesArray.controls.findIndex(
      control => control.get('date')?.value === value,
    );

    this.availabilityForm.markAsDirty();
    this.refreshCalendarEvents();
  }

  handleCalendarDateClick(arg: any): void {
    this.addOverrideFromCalendar(arg.dateStr);
  }

  handleCalendarEventClick(arg: any): void {
    arg.jsEvent.preventDefault();
    arg.jsEvent.stopPropagation();

    const dateString =
      arg.event.extendedProps?.dateString ||
      this.toDateString(arg.event.start);

    this.addOverrideFromCalendar(dateString);
  }

  handleCalendarDatesSet(arg: any): void {
    this.currentCalendarStart = arg.startStr;
    this.currentCalendarEnd = arg.endStr;

    this.refreshCalendarEvents();
  }

  refreshCalendarEvents(): void {
    if (!this.availabilityForm) return;

    const events: any[] = [];

    const startDate = this.currentCalendarStart
      ? new Date(this.currentCalendarStart)
      : new Date();

    const endDate = this.currentCalendarEnd
      ? new Date(this.currentCalendarEnd)
      : new Date(
          startDate.getFullYear(),
          startDate.getMonth() + 1,
          startDate.getDate(),
        );

    const date = new Date(startDate);

    while (date < endDate) {
      const dateString = this.toDateString(date);
      const finalRule = this.getFinalAvailabilityForDate(dateString);

      if (finalRule) {
        if (finalRule.isClosed) {
          events.push({
            title: finalRule.source === 'override'
              ? 'Override Off Day'
              : 'Off Day',
            start: dateString,
            allDay: true,
            backgroundColor: '#dc2626',
            borderColor: '#dc2626',
            textColor: '#ffffff',
            classNames: ['availability-calendar-event'],
            extendedProps: {
              dateString,
            },
          });
        } else if (finalRule.slots?.length) {
          finalRule.slots.forEach(slot => {
            events.push({
              title: `${slot.start || '--:--'} - ${slot.end || '--:--'}`,
              start: dateString,
              allDay: true,
              backgroundColor:
                finalRule.source === 'override' ? '#b45309' : '#047857',
              borderColor:
                finalRule.source === 'override' ? '#b45309' : '#047857',
              textColor: '#ffffff',
              classNames: ['availability-calendar-event'],
              extendedProps: {
                dateString,
              },
            });
          });
        }
      }

      date.setDate(date.getDate() + 1);
    }

    this.calendarOptions = {
      ...this.calendarOptions,
      events,
    };
  }

  getFinalAvailabilityForDate(dateString: string): {
    source: 'weekly' | 'override';
    isClosed: boolean;
    slots: any[];
    note?: string;
  } | null {
    const override = this.overridesArray.controls.find(control => {
      return control.get('date')?.value === dateString;
    });

    if (override) {
      return {
        source: 'override',
        isClosed: override.get('isClosed')?.value,
        slots: override.get('slots')?.value || [],
        note: override.get('note')?.value || '',
      };
    }

    const dayOfWeek = this.getDayOfWeek(dateString);

    const weeklyRule = this.weeklyArray.controls.find(control => {
      return control.get('dayOfWeek')?.value === dayOfWeek;
    });

    if (!weeklyRule) return null;

    return {
      source: 'weekly',
      isClosed: weeklyRule.get('isClosed')?.value,
      slots: weeklyRule.get('slots')?.value || [],
      note: '',
    };
  }

  getDayOfWeek(dateString: string): number {
    const date = new Date(dateString + 'T00:00:00');
    const jsDay = date.getDay();

    return jsDay === 0 ? 7 : jsDay;
  }

  toDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  sortOverrides(): void {
    const sorted = [...this.overridesArray.controls].sort((a, b) => {
      return a.get('date')?.value.localeCompare(b.get('date')?.value);
    });

    this.overridesArray.clear();

    sorted.forEach(control => {
      this.overridesArray.push(control);
    });
  }

  validateSlots(): boolean {
    const rawValue = this.availabilityForm.getRawValue();

    const allRules = [
      ...rawValue.weekly,
      ...rawValue.overrides,
    ];

    for (const rule of allRules) {
      if (rule.isClosed) continue;

      for (const slot of rule.slots || []) {
        if (!slot.start || !slot.end) {
          this.commanUsesService.showSnackBar('Please fill all start and end times.');
          return false;
        }

        if (slot.start >= slot.end) {
          this.commanUsesService.showSnackBar('Start time must be before end time.');
          return false;
        }
      }
    }

    return true;
  }

  async saveAvailability(): Promise<void> {
    if (this.availabilityForm.invalid || !this.validateSlots()) {
      this.availabilityForm.markAllAsTouched();
      return;
    }

    const rawValue = this.availabilityForm.getRawValue();

    const payload: SaveAvailabilityPayload = {
      ownerId: this.ownerId,
      ownerType: this.ownerType,
      weekly: rawValue.weekly.map(day => ({
        id: day.id,
        dayOfWeek: day.dayOfWeek,
        isClosed: day.isClosed,
        slots: day.isClosed ? [] : day.slots,
      })),
      overrides: rawValue.overrides.map(item => ({
        id: item.id,
        date: item.date,
        isClosed: item.isClosed,
        note: item.note || '',
        slots: item.isClosed ? [] : item.slots,
      })),
    };

    try {
      this.saving = true;

      const res = await this.availabilityService.saveAvailability(payload);

      this.patchAvailability(res);
      this.commanUsesService.showSnackBar('Availability saved successfully.');
    } catch (error) {
      console.error(error);
      this.commanUsesService.showSnackBar('Failed to save availability.');
    } finally {
      this.saving = false;
    }
  }

  copyWeeklySlotsFromDay(): void {
    if (!this.copyFromDayOfWeek) {
      this.commanUsesService.showSnackBar('Seleziona un giorno da cui copiare le fasce.');
      return;
    }

    if (this.copyFromDayOfWeek === this.selectedDay) {
      this.commanUsesService.showSnackBar('Seleziona un giorno diverso.');
      return;
    }

    const sourceGroup = this.weeklyArray.at(this.copyFromDayOfWeek - 1) as FormGroup;
    const sourceSlots = sourceGroup.get('slots') as FormArray;

    if (sourceGroup.get('isClosed')?.value || sourceSlots.length === 0) {
      this.commanUsesService.showSnackBar('Il giorno selezionato non ha fasce da copiare.');
      return;
    }

    const targetGroup = this.selectedWeeklyGroup;
    const targetSlots = targetGroup.get('slots') as FormArray;

    targetSlots.clear();

    sourceSlots.controls.forEach(slotControl => {
      targetSlots.push(
        this.createSlotGroup({
          start: slotControl.get('start')?.value,
          end: slotControl.get('end')?.value,
        }),
      );
    });

    targetGroup.patchValue({
      isClosed: false,
    });

    this.availabilityForm.markAsDirty();
    this.refreshCalendarEvents();

    const sourceDayName = this.days.find(
      day => day.dayOfWeek === this.copyFromDayOfWeek,
    )?.name;

    this.commanUsesService.showSnackBar(
      `Fasce copiate da ${sourceDayName || 'giorno selezionato'}.`,
    );
  }
}